import {
  Box,
  Button,
  Divider,
  Drawer,
  Typography,
  Paper,
  Tooltip,
  ClickAwayListener,
  Stack,
  TextField,
  IconButton,
} from '@mui/material';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import {
  IconCheck,
  IconCopy,
  IconEdit,
  IconPencil,
  IconTicTac,
  IconX,
} from '@tabler/icons-react';
import { shortenAddress } from '../shared/utils/Formatter';
import { useVirtualBalanceContext } from '../context/VirtualBalanceContext';
import { useForm } from 'react-hook-form';
import AssetRow from '../components/profile/AssetRow';
import { useAccount } from 'wagmi';
import { filter } from '@mdxeditor/editor';
import { EditLocation, Save, X } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser, updateUser } from '../api/user.api';
import { usePrivy } from '@privy-io/react-auth';
import { enqueueSnackbar } from 'notistack';

interface EditUserInterface {
  displayName?: string; // Optional displayName property
  avatar?: File; // Optional avatar property
}

const profilePhotoStyle: React.CSSProperties = {
  width: '140px',
  height: '140px',
  borderRadius: '50%',
  marginTop: '30px',
  marginBottom: '30px',
};

const ProfileBanner: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  walletAddress: `0x${string}` | undefined;
  onLogout: () => Promise<void>;
}> = ({ isOpen, onClose, walletAddress, onLogout }) => {
  const { virtualBalance, xVirtualBalance, ethBalance, convertionPrice } =
    useVirtualBalanceContext();

  const { user: userPrivy } = usePrivy();

  const { data: user } = useQuery({
    queryKey: ['getUser', userPrivy?.wallet?.address],
    queryFn: () => getUser(),
    enabled: !!userPrivy?.wallet?.address,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [prevDisplayName, setPrevDisplayName] = useState<string>('');

  const updateUserMutate = useMutation({
    mutationFn: updateUser,
  });

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const [Open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const onSubmitDisplayName = () => {
    const formData = new FormData();
    let trimDisplayName = displayName.split(' ');

    while (trimDisplayName[trimDisplayName.length - 1] === '') {
      trimDisplayName.pop();
    }

    if (displayName) {
      formData.append('displayName', trimDisplayName.join(' '));
    }

    updateUserMutate.mutateAsync(formData, {
      onSuccess: () => {
        setDisplayName(trimDisplayName.join(' '));
      },
      onError: (error) => {
        enqueueSnackbar({
          message: 'Display name has been taken, Please try another one',
          variant: 'error',
        });
        if (prevDisplayName) {
          setDisplayName(prevDisplayName);
        } else {
          setDisplayName('');
        }
      },
    });
  };

  const handleClose = () => {
    setDisplayName('');
    onClose();
  };

  useEffect(() => {
    if (user && user.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user, isOpen]);

  return (
    <Drawer
      anchor={'right'}
      open={isOpen}
      onClose={() => handleClose()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: {
            xs: '100vw',
            sm: '350px',
          },
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            m={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" fontWeight={'bold'}>
              Account Settings
            </Typography>

            <IconX onClick={() => handleClose()} cursor={'pointer'} />
          </Box>

          <Divider />
          <Box
            sx={{
              flexGrow: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}
          >
            <Paper
              sx={{
                flexGrow: 1,
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Stack sx={{ width: '100%', alignItems: 'center' }} spacing={2}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={
                      walletAddress !== undefined
                        ? `https://effigy.im/a/${walletAddress}.svg`
                        : ''
                    }
                    alt="profile"
                    style={profilePhotoStyle}
                  />
                </Box>

                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection={'column'}
                  spacing={1}
                  width={'100%'}
                >
                  <Typography align="center">Display name</Typography>
                  {isEditing ? (
                    <Stack
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems="center"
                      gap={1}
                      width={'100%'}
                    >
                      <TextField
                        size="small"
                        value={displayName} // Pre-fill with current value
                        variant="standard"
                        sx={{
                          flex: 1,
                        }}
                        onChange={(e) => {
                          let array = e.target.value.split(' ');
                          while (array[0] === '') {
                            array.shift();
                          }

                          let trimmedValue = array.join(' ');

                          if (trimmedValue.length <= 15) {
                            setDisplayName(trimmedValue);
                          }
                        }}
                      />
                      <IconCheck
                        fontSize="small"
                        onClick={() => {
                          onSubmitDisplayName();
                          setIsEditing(false);
                        }}
                      />
                      <IconX
                        fontSize="small"
                        onClick={() => {
                          setDisplayName(prevDisplayName);
                          setIsEditing(false);
                        }}
                      />
                    </Stack>
                  ) : (
                    <Stack
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems="center"
                      gap={1}
                    >
                      <Typography
                        align="center"
                        fontSize={'18px'}
                        fontWeight={'bold'}
                      >
                        {displayName.length > 0 ? displayName : '-'}
                      </Typography>
                      <IconPencil
                        fontSize="small"
                        onClick={() => {
                          if (displayName.length > 0) {
                            setPrevDisplayName(displayName);
                          }
                          setIsEditing(true);
                        }}
                      />
                    </Stack>
                  )}
                </Stack>

                <Stack
                  sx={{
                    border: '1px solid #F5F5F5',
                    borderRadius: 2,
                    p: 2,
                    width: '100%',
                    maxWidth: '268px',
                  }}
                  gap={3}
                >
                  <Stack gap={0.5}>
                    <Typography align="center">Wallet Address</Typography>

                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        PopperProps={{
                          disablePortal: true,
                        }}
                        onClose={handleTooltipClose}
                        open={Open}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title="Copied Wallet Address"
                        placement="top"
                        arrow={true}
                      >
                        <Typography
                          align="center"
                          fontSize={'18px'}
                          fontWeight={'bold'}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            handleCopy(walletAddress!);
                            handleTooltipOpen();
                          }}
                          onMouseLeave={() => handleTooltipClose()}
                        >
                          {walletAddress !== undefined &&
                            shortenAddress(walletAddress)}
                          <IconCopy
                            style={{ marginLeft: '10px' }}
                            stroke={1}
                            size={16}
                          />
                        </Typography>
                      </Tooltip>
                    </ClickAwayListener>
                  </Stack>
                  <Stack spacing={3}>
                    <AssetRow
                      title="ETH"
                      amount={ethBalance}
                      iconSrc="/eth-logo.png"
                      convertionValue={convertionPrice.ethereum.usd}
                    />
                    <AssetRow
                      title="Virtual"
                      amount={virtualBalance}
                      iconSrc="/virtual-logo.png"
                      convertionValue={convertionPrice['virtual-protocol'].usd}
                    />

                    <AssetRow
                      title="xVirtual"
                      amount={xVirtualBalance}
                      iconSrc="/virtual-logo.png"
                    />
                  </Stack>
                  {/* <Stack direction={'row'} sx={{ alignItems: 'center', mt: '16px', mb: '16px' }}>
                    <Button
                      variant="contained"
                      sx={{
                        width: '110px',
                        height: '37px',
                        padding: '8px, 16px, 8px, 16px',
                        borderRadius: '100px',
                        margin: 'auto',
                      }}
                      onClick={() => {
                        onClose();
                      }}
                    >
                      Withdraw
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        width: '110px',
                        height: '37px',
                        padding: '8px, 16px, 8px, 16px',
                        borderRadius: '100px',
                        margin: 'auto',
                      }}
                      onClick={() => {
                        onClose();
                      }}
                    >
                      Deposit
                    </Button>
                  </Stack> */}
                </Stack>
              </Stack>
            </Paper>
            <Paper
              sx={{
                mt: '32px',
                marginBottom: '5rem',
                display: 'flex',
                gap: 2,
              }}
            >
              <Button
                sx={{
                  width: '94px',
                  height: '37px',
                  padding: '8px, 16px, 8px, 16px',
                  borderRadius: '100px',
                  color: 'black',
                  backgroundColor: '#F5F5F5',
                  margin: 'auto',
                  ':hover': {
                    backgroundColor: '#c7c7c7',
                  },
                }}
                onClick={() => {
                  onLogout();
                  handleClose();
                }}
              >
                Log Out
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProfileBanner;
